/**
 * TreasureData.ts - Treasure chest contents and tracking
 * Maps chest IDs to rewards and tracks which chests have been opened
 */

export enum TreasureType {
  GIL = 'GIL',
  ITEM = 'ITEM',
  EQUIPMENT = 'EQUIPMENT',
  KEY_ITEM = 'KEY_ITEM',
  SPELL = 'SPELL',
}

export interface Treasure {
  id: string;
  type: TreasureType;
  itemId?: number; // Item/Equipment ID from ItemData.sol
  amount?: number; // Gil amount or quantity
  name: string;
  description: string;
  trapped?: boolean; // Some chests have enemies
  trapEnemyGroup?: number[]; // Enemy IDs if trapped
}

// =========================================================================
// ITEM ID REFERENCE (from ItemData.ts)
// =========================================================================
// Weapons: 1-15, 51-60 (Wooden Staff=1, Small Knife=2, ... Masamune=12, ...)
// Armor: 16-25 (Cloth=16, Leather=17, ... Dragon Armor=23, ...)
// Helmets: 26-31 (Cap=26, ... Ribbon=31)
// Gloves: 32-35 (Leather Gloves=32, ... Power Gauntlet=35)
// Accessories: 36-39 (Zeus Gauntlet=36, Protect Ring=37, ...)
// Consumables: 40-50 (Potion=40, Hi-Potion=41, Ether=42, Antidote=43,
//   Gold Needle=44, Phoenix Down=45, Tent=46, Sleeping Bag=47, Eye Drops=48,
//   Echo Herbs=49, Elixir=50)
// KEY_ITEM IDs: 100+ (stored in keyItems bitmask)
// =========================================================================

// Map of dungeon -> floor -> chest position -> treasure
export const TREASURE_CHESTS: Record<string, Record<number, Record<string, Treasure>>> = {
  // ========== TEMPLE OF FIENDS (Tutorial Dungeon) ==========
  TEMPLE_OF_FIENDS: {
    1: {
      '5,3': {
        id: 'TOF_1_POTION',
        type: TreasureType.ITEM,
        itemId: 40, // Potion (ID 40 in ItemData)
        amount: 1,
        name: 'Potion',
        description: 'Restores 50 HP',
      },
      '14,3': {
        id: 'TOF_1_PAS',
        type: TreasureType.GIL,
        amount: 100,
        name: '100 PAS',
        description: 'A small purse of tokens',
      },
    },
    2: {
      '10,8': {
        id: 'TOF_2_TENT',
        type: TreasureType.ITEM,
        itemId: 46, // Tent (ID 46 in ItemData)
        amount: 1,
        name: 'Tent',
        description: 'Full party heal (outside battle)',
      },
      '4,12': {
        id: 'TOF_2_PAS',
        type: TreasureType.GIL,
        amount: 200,
        name: '200 PAS',
        description: 'More tokens',
      },
    },
    3: {
      '10,10': {
        id: 'TOF_3_CRYSTAL',
        type: TreasureType.KEY_ITEM,
        itemId: 100, // Crystal (KEY_ITEM)
        amount: 1,
        name: 'Crystal',
        description: 'A mysterious glowing crystal',
      },
    },
  },

  // ========== MARSH CAVE ==========
  MARSH_CAVE: {
    1: {
      '3,5': {
        id: 'MARSH_1_PAS',
        type: TreasureType.GIL,
        amount: 400,
        name: '400 PAS',
        description: 'Tokens hidden in the marsh',
      },
      '16,8': {
        id: 'MARSH_1_ANTIDOTE',
        type: TreasureType.ITEM,
        itemId: 43, // Antidote (ID 43 in ItemData)
        amount: 3,
        name: 'Antidote x3',
        description: 'Cures poison',
      },
    },
    2: {
      '7,4': {
        id: 'MARSH_2_TRAPPED',
        type: TreasureType.GIL,
        amount: 800,
        name: '800 PAS',
        description: 'A large treasure chest',
        trapped: true,
        trapEnemyGroup: [8, 8, 8], // 3 Snakes
      },
      '12,14': {
        id: 'MARSH_2_ETHER',
        type: TreasureType.ITEM,
        itemId: 42, // Ether (ID 42 in ItemData)
        amount: 1,
        name: 'Ether',
        description: 'Restores spell charges',
      },
    },
    3: {
      '10,10': {
        id: 'MARSH_3_CROWN',
        type: TreasureType.KEY_ITEM,
        itemId: 101, // Crown (KEY_ITEM)
        amount: 1,
        name: 'Crown',
        description: 'The crown needed to prove your worth',
      },
      '5,15': {
        id: 'MARSH_3_SILVER_SWORD',
        type: TreasureType.EQUIPMENT,
        itemId: 7, // Silver Sword (ID 7 in ItemData)
        amount: 1,
        name: 'Silver Sword',
        description: 'Blessed silver blade - effective against undead',
      },
    },
  },

  // ========== EARTH CAVE (Lich's Lair) ==========
  EARTH_CAVE: {
    1: {
      '2,8': {
        id: 'EARTH_1_PAS',
        type: TreasureType.GIL,
        amount: 1000,
        name: '1000 PAS',
        description: 'A hefty sum',
      },
      '17,3': {
        id: 'EARTH_1_TENT',
        type: TreasureType.ITEM,
        itemId: 46, // Tent (ID 46 in ItemData)
        amount: 1,
        name: 'Tent',
        description: 'Rest anywhere',
      },
    },
    2: {
      '8,8': {
        id: 'EARTH_2_HI_POTION',
        type: TreasureType.ITEM,
        itemId: 41, // Hi-Potion (ID 41 in ItemData)
        amount: 2,
        name: 'Hi-Potion x2',
        description: 'Restores 150 HP',
      },
      '14,12': {
        id: 'EARTH_2_TRAPPED',
        type: TreasureType.EQUIPMENT,
        itemId: 19, // Iron Armor (ID 19 in ItemData)
        amount: 1,
        name: 'Iron Armor',
        description: 'Heavy plate armor',
        trapped: true,
        trapEnemyGroup: [13, 13], // 2 Trolls
      },
    },
    3: {
      '5,5': {
        id: 'EARTH_3_PAS',
        type: TreasureType.GIL,
        amount: 3000,
        name: '3000 PAS',
        description: 'A king\'s ransom',
      },
      '15,16': {
        id: 'EARTH_3_ZEUS_GAUNTLET',
        type: TreasureType.EQUIPMENT,
        itemId: 36, // Zeus Gauntlet (ID 36 in ItemData)
        amount: 1,
        name: 'Zeus Gauntlet',
        description: 'Lightning-charged glove',
      },
    },
    4: {
      '10,3': {
        id: 'EARTH_4_STAR_RUBY',
        type: TreasureType.KEY_ITEM,
        itemId: 102, // Star Ruby (KEY_ITEM)
        amount: 1,
        name: 'Star Ruby',
        description: 'Opens the way deeper',
      },
      '7,18': {
        id: 'EARTH_4_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 1,
        name: 'Elixir',
        description: 'Full HP/MP/status restore',
      },
    },
    5: {
      '12,12': {
        id: 'EARTH_5_PAS',
        type: TreasureType.GIL,
        amount: 5000,
        name: '5000 PAS',
        description: 'Lich\'s hoard',
      },
    },
  },

  // ========== GURGU VOLCANO (Kary's Lair) ==========
  GURGU_VOLCANO: {
    1: {
      '4,6': {
        id: 'VOLCANO_1_HI_POTION',
        type: TreasureType.ITEM,
        itemId: 41, // Hi-Potion (ID 41 in ItemData)
        amount: 3,
        name: 'Hi-Potion x3',
        description: 'Healing potions',
      },
      '15,10': {
        id: 'VOLCANO_1_PAS',
        type: TreasureType.GIL,
        amount: 2000,
        name: '2000 PAS',
        description: 'Tokens',
      },
    },
    2: {
      '8,4': {
        id: 'VOLCANO_2_TRAPPED',
        type: TreasureType.GIL,
        amount: 4000,
        name: '4000 PAS',
        description: 'A large chest',
        trapped: true,
        trapEnemyGroup: [24, 24], // 2 Golems
      },
      '13,15': {
        id: 'VOLCANO_2_FLAME_ARMOR',
        type: TreasureType.EQUIPMENT,
        itemId: 21, // Flame Armor (ID 21 in ItemData)
        amount: 1,
        name: 'Flame Armor',
        description: 'Fire-resistant armor',
      },
    },
    3: {
      '5,8': {
        id: 'VOLCANO_3_ICE_BRAND',
        type: TreasureType.EQUIPMENT,
        itemId: 8, // Ice Brand (ID 8 in ItemData)
        amount: 1,
        name: 'Ice Brand',
        description: 'Frozen magical blade',
      },
      '16,7': {
        id: 'VOLCANO_3_PHOENIX_DOWN',
        type: TreasureType.ITEM,
        itemId: 45, // Phoenix Down (ID 45 in ItemData)
        amount: 2,
        name: 'Phoenix Down x2',
        description: 'Revives fallen ally',
      },
    },
    4: {
      '10,10': {
        id: 'VOLCANO_4_PAS',
        type: TreasureType.GIL,
        amount: 8000,
        name: '8000 PAS',
        description: 'Volcanic treasure',
      },
      '3,17': {
        id: 'VOLCANO_4_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 1,
        name: 'Elixir',
        description: 'Full restoration',
      },
    },
    5: {
      '12,12': {
        id: 'VOLCANO_5_FIRE_CRYSTAL',
        type: TreasureType.KEY_ITEM,
        itemId: 103, // Fire Crystal (KEY_ITEM)
        amount: 1,
        name: 'Fire Crystal',
        description: 'Restores the Fire Crystal',
      },
    },
  },

  // ========== ICE CAVE ==========
  ICE_CAVE: {
    1: {
      '6,4': {
        id: 'ICE_1_PAS',
        type: TreasureType.GIL,
        amount: 3000,
        name: '3000 PAS',
        description: 'Frozen tokens',
      },
      '14,14': {
        id: 'ICE_1_ICE_ARMOR',
        type: TreasureType.EQUIPMENT,
        itemId: 22, // Ice Armor (ID 22 in ItemData)
        amount: 1,
        name: 'Ice Armor',
        description: 'Frost-resistant armor',
      },
    },
    2: {
      '9,8': {
        id: 'ICE_2_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 1,
        name: 'Elixir',
        description: 'Full restoration',
      },
      '4,16': {
        id: 'ICE_2_TRAPPED',
        type: TreasureType.GIL,
        amount: 6000,
        name: '6000 PAS',
        description: 'Icy treasure',
        trapped: true,
        trapEnemyGroup: [16, 16, 16], // 3 Wraiths
      },
    },
    3: {
      '10,10': {
        id: 'ICE_3_LEVISTONE',
        type: TreasureType.KEY_ITEM,
        itemId: 104, // Levistone (KEY_ITEM)
        amount: 1,
        name: 'Levistone',
        description: 'Makes heavy things float',
      },
      '15,5': {
        id: 'ICE_3_EXCALIBUR',
        type: TreasureType.EQUIPMENT,
        itemId: 58, // Excalibur (ID 58 in ItemData)
        amount: 1,
        name: 'Excalibur',
        description: 'Ultimate holy blade',
      },
    },
  },

  // ========== SEA SHRINE (Kraken's Lair) ==========
  SEA_SHRINE: {
    1: {
      '5,7': {
        id: 'SEA_1_SCIMITAR',
        type: TreasureType.EQUIPMENT,
        itemId: 5, // Scimitar (ID 5 in ItemData)
        amount: 1,
        name: 'Scimitar',
        description: 'Curved blade',
      },
      '16,4': {
        id: 'SEA_1_PAS',
        type: TreasureType.GIL,
        amount: 5000,
        name: '5000 PAS',
        description: 'Sunken treasure',
      },
    },
    2: {
      '8,12': {
        id: 'SEA_2_RUBY_RING',
        type: TreasureType.EQUIPMENT,
        itemId: 38, // Ruby Ring (ID 38 in ItemData)
        amount: 1,
        name: 'Ruby Ring',
        description: 'Fire resistance ring',
      },
      '14,8': {
        id: 'SEA_2_HI_POTION',
        type: TreasureType.ITEM,
        itemId: 41, // Hi-Potion (ID 41 in ItemData)
        amount: 5,
        name: 'Hi-Potion x5',
        description: 'Healing supplies',
      },
    },
    3: {
      '6,15': {
        id: 'SEA_3_TRAPPED',
        type: TreasureType.GIL,
        amount: 10000,
        name: '10000 PAS',
        description: 'Kraken\'s tokens',
        trapped: true,
        trapEnemyGroup: [21, 21], // 2 Medusas
      },
      '13,6': {
        id: 'SEA_3_RIBBON',
        type: TreasureType.EQUIPMENT,
        itemId: 31, // Ribbon (ID 31 in ItemData)
        amount: 1,
        name: 'Ribbon',
        description: 'Prevents all status effects',
      },
    },
    4: {
      '10,10': {
        id: 'SEA_4_MASAMUNE',
        type: TreasureType.EQUIPMENT,
        itemId: 12, // Masamune (ID 12 in ItemData)
        amount: 1,
        name: 'Masamune',
        description: 'Legendary katana',
      },
      '3,18': {
        id: 'SEA_4_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 2,
        name: 'Elixir x2',
        description: 'Full restoration',
      },
    },
    5: {
      '12,12': {
        id: 'SEA_5_WATER_CRYSTAL',
        type: TreasureType.KEY_ITEM,
        itemId: 105, // Water Crystal (KEY_ITEM)
        amount: 1,
        name: 'Water Crystal',
        description: 'Restores the Water Crystal',
      },
    },
  },

  // ========== MIRAGE TOWER ==========
  MIRAGE_TOWER: {
    1: {
      '7,7': {
        id: 'MIRAGE_1_PAS',
        type: TreasureType.GIL,
        amount: 7000,
        name: '7000 PAS',
        description: 'Mirage tokens',
      },
      '13,13': {
        id: 'MIRAGE_1_DRAGON_ARMOR',
        type: TreasureType.EQUIPMENT,
        itemId: 23, // Dragon Armor (ID 23 in ItemData)
        amount: 1,
        name: 'Dragon Armor',
        description: 'Dragon scale armor',
      },
    },
    2: {
      '10,5': {
        id: 'MIRAGE_2_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 2,
        name: 'Elixir x2',
        description: 'Full restoration',
      },
      '5,15': {
        id: 'MIRAGE_2_TRAPPED',
        type: TreasureType.EQUIPMENT,
        itemId: 37, // Protect Ring (ID 37 in ItemData)
        amount: 1,
        name: 'Protect Ring',
        description: 'Powerful defense ring',
        trapped: true,
        trapEnemyGroup: [35, 36], // Death Eye + Evil Eye
      },
    },
    3: {
      '10,10': {
        id: 'MIRAGE_3_ADAMANT',
        type: TreasureType.KEY_ITEM,
        itemId: 106, // Adamant (KEY_ITEM)
        amount: 1,
        name: 'Adamant',
        description: 'The hardest metal',
      },
    },
  },

  // ========== SKY CASTLE (Tiamat's Lair) ==========
  SKY_CASTLE: {
    1: {
      '8,4': {
        id: 'SKY_1_PAS',
        type: TreasureType.GIL,
        amount: 15000,
        name: '15000 PAS',
        description: 'Sky treasure',
      },
      '12,16': {
        id: 'SKY_1_SAPPHIRE_RING',
        type: TreasureType.EQUIPMENT,
        itemId: 39, // Sapphire Ring (ID 39 in ItemData)
        amount: 1,
        name: 'Sapphire Ring',
        description: 'Ice resistance ring',
      },
    },
    2: {
      '5,8': {
        id: 'SKY_2_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 3,
        name: 'Elixir x3',
        description: 'Full restoration',
      },
      '15,12': {
        id: 'SKY_2_SUN_BLADE',
        type: TreasureType.EQUIPMENT,
        itemId: 11, // Sun Blade (ID 11 in ItemData)
        amount: 1,
        name: 'Sun Blade',
        description: 'Radiant holy sword',
      },
    },
    3: {
      '10,6': {
        id: 'SKY_3_TRAPPED',
        type: TreasureType.GIL,
        amount: 25000,
        name: '25000 PAS',
        description: 'Massive treasure',
        trapped: true,
        trapEnemyGroup: [38, 38], // 2 Demons
      },
      '7,14': {
        id: 'SKY_3_WHITE_ROBE',
        type: TreasureType.EQUIPMENT,
        itemId: 25, // White Robe (ID 25 in ItemData)
        amount: 1,
        name: 'White Robe',
        description: 'White mage robes',
      },
    },
    4: {
      '12,8': {
        id: 'SKY_4_VORPAL_SWORD',
        type: TreasureType.EQUIPMENT,
        itemId: 57, // Vorpal Sword (ID 57 in ItemData)
        amount: 1,
        name: 'Vorpal Sword',
        description: 'Rare instant-death blade',
      },
      '4,15': {
        id: 'SKY_4_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 2,
        name: 'Elixir x2',
        description: 'Full restoration',
      },
    },
    5: {
      '10,10': {
        id: 'SKY_5_WIND_CRYSTAL',
        type: TreasureType.KEY_ITEM,
        itemId: 107, // Wind Crystal (KEY_ITEM)
        amount: 1,
        name: 'Wind Crystal',
        description: 'Restores the Wind Crystal',
      },
    },
  },

  // ========== TEMPLE OF FIENDS REVISITED (Final Dungeon) ==========
  TEMPLE_FIENDS_REVISITED: {
    1: {
      '5,5': {
        id: 'TOF_PAST_1_PAS',
        type: TreasureType.GIL,
        amount: 30000,
        name: '30000 PAS',
        description: 'Ancient wealth',
      },
    },
    2: {
      '10,10': {
        id: 'TOF_PAST_2_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 5,
        name: 'Elixir x5',
        description: 'Full restoration',
      },
    },
    3: {
      '8,12': {
        id: 'TOF_PAST_3_TRAPPED',
        type: TreasureType.EQUIPMENT,
        itemId: 58, // Excalibur (ID 58 in ItemData - best sword available)
        amount: 1,
        name: 'Excalibur',
        description: 'Ultimate holy blade',
        trapped: true,
        trapEnemyGroup: [33], // Warmech
      },
    },
    4: {
      '12,6': {
        id: 'TOF_PAST_4_BLACK_ROBE',
        type: TreasureType.EQUIPMENT,
        itemId: 24, // Black Robe (ID 24 in ItemData)
        amount: 1,
        name: 'Black Robe',
        description: 'Black mage robes',
      },
    },
    5: {
      '7,15': {
        id: 'TOF_PAST_5_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 10,
        name: 'Elixir x10',
        description: 'Ultimate healing cache',
      },
    },
    6: {
      '10,10': {
        id: 'TOF_PAST_6_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 3,
        name: 'Elixir x3',
        description: 'Full restoration',
      },
    },
    7: {
      '5,8': {
        id: 'TOF_PAST_7_DRAGON_ARMOR',
        type: TreasureType.EQUIPMENT,
        itemId: 23, // Dragon Armor (ID 23 in ItemData)
        amount: 1,
        name: 'Dragon Armor',
        description: 'Dragon scale armor',
      },
    },
    8: {
      '15,12': {
        id: 'TOF_PAST_8_DRAGON_HELMET',
        type: TreasureType.EQUIPMENT,
        itemId: 30, // Dragon Helmet (ID 30 in ItemData)
        amount: 1,
        name: 'Dragon Helmet',
        description: 'Dragon bone helm',
      },
    },
    9: {
      '10,5': {
        id: 'TOF_PAST_9_POWER_GAUNTLET',
        type: TreasureType.EQUIPMENT,
        itemId: 35, // Power Gauntlet (ID 35 in ItemData)
        amount: 1,
        name: 'Power Gauntlet',
        description: 'Strength-enhancing gloves',
      },
    },
    10: {
      '12,18': {
        id: 'TOF_PAST_10_ELIXIR',
        type: TreasureType.ITEM,
        itemId: 50, // Elixir (ID 50 in ItemData)
        amount: 20,
        name: 'Elixir x20',
        description: 'Final preparations',
      },
    },
  },
};

/**
 * Treasure tracking - stores which chests have been opened
 */
export class TreasureTracker {
  private openedChests: Set<string> = new Set();

  /**
   * Mark a chest as opened
   */
  openChest(chestId: string): void {
    this.openedChests.add(chestId);
  }

  /**
   * Check if a chest has been opened
   */
  isOpened(chestId: string): boolean {
    return this.openedChests.has(chestId);
  }

  /**
   * Get treasure at position in dungeon
   */
  getTreasureAt(
    dungeonId: string,
    floor: number,
    x: number,
    y: number
  ): Treasure | null {
    const posKey = `${x},${y}`;
    const dungeon = TREASURE_CHESTS[dungeonId];
    if (!dungeon) return null;

    const floorData = dungeon[floor];
    if (!floorData) return null;

    return floorData[posKey] ?? null;
  }

  /**
   * Check if there's a chest at position
   */
  hasChestAt(dungeonId: string, floor: number, x: number, y: number): boolean {
    return this.getTreasureAt(dungeonId, floor, x, y) !== null;
  }

  /**
   * Get unopened treasure at position
   */
  getUnopenedTreasureAt(
    dungeonId: string,
    floor: number,
    x: number,
    y: number
  ): Treasure | null {
    const treasure = this.getTreasureAt(dungeonId, floor, x, y);
    if (!treasure || this.isOpened(treasure.id)) return null;
    return treasure;
  }

  /**
   * Save state to localStorage
   */
  save(): void {
    localStorage.setItem('ff1_opened_chests', JSON.stringify([...this.openedChests]));
  }

  /**
   * Load state from localStorage
   */
  load(): void {
    const saved = localStorage.getItem('ff1_opened_chests');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        this.openedChests = new Set(data);
      } catch (e) {
        console.error('Failed to load treasure data:', e);
      }
    }
  }

  /**
   * Reset all opened chests (new game)
   */
  reset(): void {
    this.openedChests.clear();
    this.save();
  }

  /**
   * Get total chests opened
   */
  getTotalOpened(): number {
    return this.openedChests.size;
  }

  /**
   * Get completion percentage for a dungeon
   */
  getDungeonCompletion(dungeonId: string): number {
    const dungeon = TREASURE_CHESTS[dungeonId];
    if (!dungeon) return 0;

    let total = 0;
    let opened = 0;

    for (const floor of Object.values(dungeon)) {
      for (const treasure of Object.values(floor)) {
        total++;
        if (this.isOpened(treasure.id)) {
          opened++;
        }
      }
    }

    return total > 0 ? Math.floor((opened / total) * 100) : 0;
  }
}

// Singleton instance
export const treasureTracker = new TreasureTracker();
